import React, { FC, useCallback, useEffect, useState, useRef } from 'react'

const ORDER_ROW_HEIGHT = 102
const UNAVAILABLE_SECTION_GAP = 32
import { reject, sumBy } from 'lodash/fp'
import { RouteComponentProps } from 'react-router'
import { format } from 'date-fns'
import AutoSizer from 'react-virtualized-auto-sizer'
import { VariableSizeList } from 'react-window'
import useThunkDispatch from '../../../common/useThunkDispatch'
import Modal from '../../../components/Modal'
import Button from '../../../components/Button'
import Splash from '../../../components/Splash'
import Search from '../../../components/Search'
import {
  fetchFlowAccessForShoppingCart,
  ICartItem,
  IDetailsCart,
} from '../../../types/orders'
import { cartTotal } from '../../../common/orders'
import withAccountAndBranch from '../../../common/withAccountAndBranch'
import { searchProducts } from '../../../accounts/accountsActions'
import { IAccount, IBranch, IUser } from '../../../types/user'
import { IProduct } from '../../../types/products'
import {
  requestOrderSubmitFromCart,
  requestUpdateCartItem,
  requestCreateCartItem,
  requestOrderRequestFromCart,
  requestCartBranchUpdate,
} from '../ordersActions'
import { useOrderPONumberModal } from './OrderPONumber'
import { useOrderRequestModal } from './OrderRequest'
import { useAmountDrafts } from '../../../common/useAmountDrafts'
import { find } from 'lodash'
import ShoppingCartHeader from './ShoppingCartHeader'
import ShoppingCartForecast from './ShoppingCartForecast'
import ShoppingCartRow from '../ShoppingCartRow'
import ShoppingCartProductRow from '../ShoppingCartProductRow'
import UnavailableProductsBanner from '../UnavailableProductsBanner'
import { requestSetManagerBranch } from '../../managerActions'

interface IOrderRouteParams
  extends RouteComponentProps<{
    accountId?: string
    branchId?: string
    returnUrl?: string
  }> {
  user: IUser
  account?: IAccount
  branch?: IBranch
}

const ShoppingCart: FC<IOrderRouteParams> = ({
  user,
  account: currentAccount,
  branch,
  location,
  history,
}) => {
  const dispatch = useThunkDispatch()
  const [term, setTerm] = useState<string>('')
  const [debouncedTerm, setDebouncedTerm] = useState<string>('')
  const activeRequestId = useRef(0)
  const [foundProducts, setFoundProducts] = useState<IProduct[] | null>(null)
  const [searching, setSearching] = useState<boolean>(false)
  const [requesting, setRequesting] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [updating, setUpdating] = useState<boolean>(false)
  const listRef = useRef<VariableSizeList>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [autoEditIndex, setAutoEditIndex] = useState<number | null>(null)
  const account = currentAccount || user.account
  const nothingFound = foundProducts && !foundProducts.length && !searching

  const loading = searching || requesting || submitting || updating

  const [cartBranch, setCartBranch] = useState(branch)
  const [poNumber, setPoNumber] = useState<string>('')
  const flowAccess =
    cartBranch && account
      ? fetchFlowAccessForShoppingCart(account, user, cartBranch)
      : {}
  const isEditable = flowAccess.canEdit
  let cartItems =
    (cartBranch && reject({ amountRequested: 0 }, cartBranch.cartItems)) || []
  cartItems = (
    (cartBranch && reject({ suggestion: true }, cartBranch.cartItems)) ||
    []
  ).filter((item) => item.product)

  const unavailableCartItems = cartItems.filter(
    (item) => !!item.product.unavailable,
  )
  const availableCartItems = cartItems.filter(
    (item) => !item.product.unavailable,
  )

  const sortedCartItems = [
    ...unavailableCartItems.sort((a, b) =>
      a.product.name.localeCompare(b.product.name),
    ),
    ...availableCartItems.sort((a, b) =>
      a.product.name.localeCompare(b.product.name),
    ),
  ]

  const hasUnavailableItems = unavailableCartItems.length > 0
  const isEmpty = sumBy('amountRequested', sortedCartItems) === 0

  useEffect(() => {
    setCartBranch(branch)
  }, [branch])

  useEffect(() => {
    if (!cartBranch) return

    const fetchCartBranchAndPopulate = async () => {
      const params = new URLSearchParams(location.search)
      const populate = params.get('populate')

      if (
        populate === 'true' &&
        isEmpty &&
        branch &&
        branch.suggestedCartItems &&
        branch.suggestedCartItems.length > 0
      ) {
        try {
          handleAddSuggestedCartItemsToCart()
          params.delete('populate')
          history.replace({
            pathname: location.pathname,
            search: params.toString(),
          })
        } catch (error) {
          console.error('Error populating cart:', error)
        }
      }
    }
    fetchCartBranchAndPopulate()
  }, [cartBranch])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTerm(term)
    }, 300)

    return () => clearTimeout(handler)
  }, [term])

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true)
    }
  }, [foundProducts])

  useEffect(() => {
    if (!debouncedTerm) {
      setFoundProducts(null)
      return
    }

    const performSearch = async () => {
      const requestId = ++activeRequestId.current
      setSearching(true)

      const results = await dispatch(
        searchProducts(
          account ? account.id : user.account!.id,
          cartBranch!.id,
          debouncedTerm,
        ),
      )
      if (requestId !== activeRequestId.current) return

      setFoundProducts(results)

      if (listRef.current) listRef.current.scrollTo(0)
      setSearching(false)
    }

    performSearch().catch((error) => {
      console.error('performSearch error:', error)
    })
  }, [debouncedTerm])

  // on back
  const handleBack = useCallback(() => {
    const returnTo = location.state?.returnTo
    if (returnTo) {
      history.push(returnTo)
    } else {
      history.push('/orders')
    }
  }, [location.state])

  // on update
  const handleUpdate = useCallback(
    (attrs: Partial<IDetailsCart>) => {
      const perform = async () => {
        setUpdating(true)
        const { result, errors } = await dispatch(
          requestCartBranchUpdate(cartBranch!.id, attrs),
        )
        setUpdating(false)

        if (result) {
          setCartBranch({ ...cartBranch, ...result })
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [cartBranch],
  )

  // save a cart item amount; used by the draft autosave and by create
  const saveCartItemAmount = useCallback(
    async (item: ICartItem, amountRequested: number) => {
      setUpdating(true)
      const res = await dispatch(
        requestUpdateCartItem(cartBranch!, { ...item, amountRequested }),
      )
      setUpdating(false)

      if (res.result) {
        setCartBranch(res.result)
      } else if (res.errors) {
        alert(res.errors)
      }
      return res
    },
    [cartBranch],
  )

  // Set below, once handleCartItemDelete exists — it needs clearDraft from this hook.
  const removeRef = useRef<(item: ICartItem) => void>()

  const { drafts, onAmountChange, flushItem, flush, clearDraft } =
    useAmountDrafts(
      (item, amount) => saveCartItemAmount(item as ICartItem, amount),
      (item) => {
        if (!confirm('Are you sure you want to remove the item from cart?'))
          return
        removeRef.current?.(item as ICartItem)
      },
    )

  // on cart item create
  const handleCartItemCreate = useCallback(
    (product: IProduct) => {
      const perform = async () => {
        const existingCartItem = cartItems!.find(
          (oi) => oi.product?.id === product.id,
        )

        if (existingCartItem) {
          saveCartItemAmount(existingCartItem, 1)
          return
        }

        setUpdating(true)
        const { result, errors } = await dispatch(
          requestCreateCartItem(cartBranch!, { amountRequested: 1, product }),
        )
        setUpdating(false)

        if (result) {
          setCartBranch(result)
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [cartBranch, saveCartItemAmount],
  )

  // on cart item remove
  const handleCartItemDelete = useCallback(
    (item: ICartItem) => {
      const perform = async () => {
        clearDraft(item.id)
        setUpdating(true)
        const { result, errors } = await dispatch(
          requestUpdateCartItem(cartBranch!, { ...item, amountRequested: 0 }),
        )
        setUpdating(false)

        if (result) {
          setCartBranch(result)
        } else if (errors) {
          alert(errors)
        }
      }

      perform()
    },
    [cartBranch, clearDraft],
  )

  removeRef.current = handleCartItemDelete

  const handleFocusNextRow = useCallback(
    (index: number) => {
      const total = foundProducts
        ? foundProducts.length
        : sortedCartItems.length
      const canEditRow = (i: number) => {
        const item = foundProducts
          ? sortedCartItems.find((ci) => ci.product.id === foundProducts[i].id)
          : sortedCartItems[i]
        return isEditable && !!item && !item.product.unavailable
      }

      let nextIndex = index + 1
      while (nextIndex < total && !canEditRow(nextIndex)) {
        nextIndex += 1
      }

      if (nextIndex >= total) {
        searchInputRef.current?.focus()
        return
      }

      listRef.current?.scrollToItem(nextIndex)
      setAutoEditIndex(nextIndex)
    },
    [foundProducts, sortedCartItems, isEditable],
  )

  // on cart item request
  const handleRequest = useCallback(() => {
    const perform = async () => {
      setRequesting(true)
      if (!(await flush())) {
        setRequesting(false)
        return
      }
      const { result, errors } = await dispatch(
        requestOrderRequestFromCart(cartBranch!.id),
      )
      setRequesting(false)

      if (result) {
        setCartBranch(result.branch)
        handleBack()
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [cartBranch, handleBack, flush])

  // on order submit
  const handleSubmit = useCallback(() => {
    const perform = async () => {
      setSubmitting(true)
      if (!(await flush())) {
        setSubmitting(false)
        return
      }
      const { result, errors } = await dispatch(
        requestOrderSubmitFromCart(cartBranch!.id),
      )
      setSubmitting(false)

      if (result) {
        setCartBranch(result.branch)
        handleBack()
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [cartBranch, handleBack, flush])

  const handleAddSuggestedCartItemsToCart = useCallback(() => {
    const perform = async () => {
      setUpdating(true)
      const { result, errors } = await dispatch(
        requestCartBranchUpdate(cartBranch!.id, {
          moveSuggestedCartItemsToCart: true,
        }),
      )
      setUpdating(false)

      if (result) {
        await dispatch(requestSetManagerBranch(result))
        setCartBranch(result)
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [cartBranch, handleBack])

  // on clear cart
  const handleClearCart = useCallback(() => {
    const perform = async () => {
      setUpdating(true)
      const { result, errors } = await dispatch(
        requestCartBranchUpdate(cartBranch!.id, { clearCartItems: true }),
      )
      setUpdating(false)

      if (result) {
        await dispatch(requestSetManagerBranch(result))
        setCartBranch(result)
      } else if (errors) {
        alert(errors)
      }
    }

    perform()
  }, [cartBranch])

  // request
  const [{ open: openRequestModal, close: closeRequestModal }, requestModal] =
    useOrderRequestModal({
      onRequest: () => {
        handleRequest()
        closeRequestModal()
      },
    })

  // po number
  const [
    { open: openPONumberModal, close: closePONumberModal },
    poNumberModal,
  ] = useOrderPONumberModal({
    poNumber,
    onSave: (poNumber: string) => {
      handleUpdate({ purchaseOrderNumber: poNumber })
      setPoNumber(poNumber)
      closePONumberModal()
    },
  })

  if (!branch || !cartBranch) return <Splash isLoading />

  const multiCatalogEnabled = (account?.erpCatalogs || []).length > 1
  const showLowStock =
    isEditable &&
    !!cartItems!.length &&
    !!find(
      cartItems,
      (oi) =>
        oi.product?.stockInventory !== undefined &&
        oi.amountRequested &&
        oi.amountRequested > oi.product!.stockInventory,
    )
  return (
    <Modal
      closeable={false}
      containerClassName=""
      modalClassName="cart-modal"
      testId="cart-modal"
    >
      <div className="order-modal">
        <ShoppingCartHeader
          branch={cartBranch}
          onNumberEdit={openPONumberModal}
          onClose={() => {
            handleBack()
            dispatch(requestSetManagerBranch(branch!))
          }}
        />
        <div className="form">
          <ShoppingCartForecast
            branch={branch}
            updating={updating}
            onAddToOrder={handleAddSuggestedCartItemsToCart}
          />

          <div className="order-modal_search">
            <div className="order-modal_field">
              <Search
                ref={searchInputRef}
                searching={searching}
                id="cart-search"
                placeholder="Search item name or ID to add"
                value={term}
                onChange={(e) =>
                  setTerm(e.target.value ? e.target.value.trim() : '')
                }
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault()
                    handleFocusNextRow(-1)
                  }
                }}
              />
            </div>
            <div className="order-modal_total">
              Total
              <br />
              <strong>{cartTotal(cartBranch)}</strong>
            </div>
          </div>
          {hasUnavailableItems && (
            <UnavailableProductsBanner
              onGoToPlan={() => history.push('/plan')}
            />
          )}
          {showLowStock && (
            <div className="order-modal_alert">
              Some items could be delayed due to a lack of availability -
              Forshaw CSR will be in touch to discuss
            </div>
          )}
          <div className="order-modal_list">
            {nothingFound && (
              <div className="table">
                <div className="table_head-tr">
                  <div className="table_head-tr table_td table_title">
                    No products found
                  </div>
                </div>
              </div>
            )}
            <AutoSizer
              className="table"
              style={{ height: 'calc(100vh - 265px)' }}
            >
              {(size) => {
                const unavailableCount = unavailableCartItems.length
                const getItemSize = (index: number) => {
                  if (foundProducts) return ORDER_ROW_HEIGHT
                  const isLastUnavailable =
                    unavailableCount > 0 && index === unavailableCount - 1
                  return isLastUnavailable
                    ? ORDER_ROW_HEIGHT + UNAVAILABLE_SECTION_GAP
                    : ORDER_ROW_HEIGHT
                }
                return (
                  <VariableSizeList
                    ref={listRef}
                    className="wonder-scroll"
                    {...size}
                    itemData={{
                      editable: isEditable,
                      products: foundProducts,
                      onAdd: handleCartItemCreate,
                      drafts,
                      onAmountChange,
                      onAmountFlush: flushItem,
                      onDelete: handleCartItemDelete,
                      onFocusNextRow: handleFocusNextRow,
                      onFocusRow: setAutoEditIndex,
                      autoEditIndex,
                      onAutoEditHandled: () => setAutoEditIndex(null),
                      cartItems: sortedCartItems,
                      unavailableCount: foundProducts ? 0 : unavailableCount,
                      multiCatalogEnabled,
                      loading,
                    }}
                    itemCount={
                      foundProducts
                        ? foundProducts.length
                        : sortedCartItems.length
                    }
                    itemSize={getItemSize}
                    itemKey={(index) =>
                      foundProducts
                        ? foundProducts[index].id
                        : sortedCartItems[index].id
                    }
                  >
                    {foundProducts ? ShoppingCartProductRow : ShoppingCartRow}
                  </VariableSizeList>
                )
              }}
            </AutoSizer>
          </div>
        </div>
        <div className="form_controls">
          <div className="order-modal_footer form_controls-container">
            {
              <Button
                onClick={handleClearCart}
                color="danger"
                className="form_control -size -left"
                data-testid="cart-clear-order"
              >
                Clear order
              </Button>
            }

            <div className="order-modal_footer_update_info">
              {(() => {
                if (!cartBranch?.cartItems?.length) {
                  return ''
                }

                const lastCartItem = cartBranch.cartItems.at(-1)

                const updatedAt = lastCartItem?.updatedAt
                  ? format(new Date(lastCartItem.updatedAt), 'MMM DD, YYYY')
                  : 'n/a'

                const lastCartItemUpdatedBy = lastCartItem?.updatedBy
                const name =
                  lastCartItemUpdatedBy?.name === 'System user'
                    ? 'ForeSight'
                    : lastCartItemUpdatedBy?.name || 'n/a'

                return `Updated ${updatedAt} by ${name}`
              })()}
            </div>

            {flowAccess.canRequest && (
              <Button
                type="button"
                color="success"
                disabled={requesting || hasUnavailableItems}
                className="form_control"
                onClick={openRequestModal}
                data-testid="cart-request-approval"
              >
                Request approval
              </Button>
            )}
            {flowAccess.canSubmit && (
              <Button
                type="button"
                color="success"
                disabled={submitting || hasUnavailableItems}
                className="form_control"
                onClick={handleSubmit}
                data-testid="cart-place-order"
              >
                Place order
              </Button>
            )}
          </div>
        </div>
      </div>
      {poNumberModal}
      {requestModal}
    </Modal>
  )
}

export default withAccountAndBranch(ShoppingCart)
